import React, {
  useCallback, useEffect, useState
} from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQuery } from 'react-apollo'
import { useForm } from 'react-hook-form'
import { Redirect } from 'react-router-dom'

import ProfilesForm from '../components/profiles-form'
import { useConfig } from '../../../hooks/use-config'
import { useIsAuthenticatedAndRedirect } from '../../../hooks/use-is-authenticated-and-redirect'
import { useMutateAndLoading } from '../../../hooks/use-mutate-and-loading'
import { useRequirePin } from '../../../hooks/use-require-pin'
import { useProfileOptions } from '../hooks/use-profile-options'
import { getGQLErrorMsg } from '../../../lib/apollo'
import { getModalLocation } from '../../../lib/modal'
import { getDefaultParentalRating } from '../../../lib/parental-ratings'
import { segmentTrackAddProfile } from '../../../segment/segment-track'
import { getProfileByName } from '../../../utils/getProfileByName'
import { PROFILES, MODALS } from '../../../constants'
import { LoadingModal } from '../../../components/loading/modal'
import ACCOUNT_QUERY from '../../../../graphql/queries/account.gql'
import PROFILE_MUTATION from '../../../../graphql/mutations/profile.gql'
import PROFILE_PREFERENCES_MUTATION from '../../../../graphql/mutations/update-profile-preferences.gql'

/**
 * Displays a form for adding a new profile.
 * Handles form initialization, submission and error handling.
 * Redirects the user to the profile management screen upon successful submission.
 */
function AddProfiles({ history, location }) {
  const [isAvatarSelected, setIsAvatarSelected] = useState(false)
  const [formError, setFormError] = useState('')
  const {
    isAuthenticated,
    shouldRedirect,
    redirectTo
  } = useIsAuthenticatedAndRedirect(location)
  const { data, loading, error } = useQuery(ACCOUNT_QUERY, {
    skip: !isAuthenticated
  })
  const {
    appConfig,
    parentalRatingsData,
    ageGroupsData,
    configLoading,
    configError
  } = useConfig()
  const [mutateProfile] = useMutation(PROFILE_MUTATION, {
    update: (proxy, response) => {
      const accountData = proxy.readQuery({ query: ACCOUNT_QUERY })
      const profiles = response.data?.profile?.profiles || accountData.account.profiles

      proxy.writeQuery({
        query: ACCOUNT_QUERY,
        data: {
          ...accountData,
          account: {
            ...accountData.account,
            profiles
          }
        }
      })
    }
  })
  const [mutateProfilePreferences] = useMutation(PROFILE_PREFERENCES_MUTATION)
  const mutateAndLoading = useMutateAndLoading(mutateProfile)
  const account = data && data.account
  const {
    ageGroupOptions,
    birthYearOptions,
    parentalRatingOptions
  } = useProfileOptions({ ageGroupsData, appConfig, parentalRatingsData })
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    getValues,
    clearErrors,
    formState: {
      errors,
      touchedFields,
      isSubmitted,
      isSubmitting
    }
  } = useForm({
    defaultValues: {
      isKid: false,
      isDefault: false,
      avatar: null,
      name: '',
      ageGroup: undefined,
      birthYear: '',
      gender: '',
      parentalRating: undefined,
      email: '',
      mobile: '',
      nextEpisodeAutoplay: true,
      trailerAutoplayOnBrowsePage: true,
      trailerAutoplayOnShowPage: true
    },
    mode: 'onChange'
  })

  useRequirePin(account)

  useEffect(() => {
    if (parentalRatingsData && !getValues('parentalRating')) {
      setValue(
        'parentalRating',
        getDefaultParentalRating(parentalRatingsData)
      )
    }
  }, [getValues, parentalRatingsData, setValue])

  const onCancel = useCallback(() => {
    if (location.state?.from) {
      history.goBack()
      return
    }

    history.push(getModalLocation(
      location,
      MODALS.qsParams.profilesManagement,
      null,
      { from: false }
    ))
  }, [history, location])

  const onSubmit = async values => {
    setFormError('')
    clearErrors()

    try {
      const profileData = await mutateAndLoading('profilesAdd', {
        variables: {
          input: {
            isKid: values.isKid,
            isDefault: values.isDefault,
            name: values.name.trim(),
            email: values.isKid ? '' : values.email,
            avatar: values.avatar?.id,
            mobile: values.isKid ? '' : values.mobile.trim(),
            ageGroup: values.isKid ? values.ageGroup?.value.id : undefined,
            maxRating: values.parentalRating?.value || values.parentalRating?.rating,
            birthYear: values.birthYear?.value,
            gender: values.gender?.value
          }
        }
      })
      const mutationResponse = { data: profileData }
      const addedProfile = getProfileByName(mutationResponse, values.name)

      await mutateProfilePreferences({
        variables: {
          input: {
            profileId: addedProfile.id,
            trailerAutoplayOnShowPageEnabled: values.trailerAutoplayOnShowPage,
            trailerAutoplayOnBrowsePageEnabled: values.trailerAutoplayOnBrowsePage,
            nextEpisodeAutoplayEnabled: values.nextEpisodeAutoplay
          }
        }
      })

      if (addedProfile) {
        const {
          id, name, gender, birthYear, email, mobile, isKid, maxRating
        } = addedProfile

        segmentTrackAddProfile({
          id,
          name,
          gender,
          birthYear,
          email,
          mobile,
          isKid,
          maxRating
        })
      }

      onCancel()
    } catch (submitError) {
      const graphQLErrors = submitError.graphQLErrors || []
      const nameError = graphQLErrors.find(
        graphQLError => graphQLError.code === 'PROFILE_NAME_DUPLICATE_ERROR'
      )

      if (nameError) {
        setError('name', {
          type: 'server',
          message: nameError.message
        }, {
          shouldFocus: true
        })
        return
      }

      setFormError(getGQLErrorMsg(submitError))
    }
  }

  if (shouldRedirect) {
    return <Redirect to={redirectTo} />
  }

  if (
    loading ||
    error ||
    configLoading ||
    configError ||
    !account ||
    !appConfig.avatars ||
    !parentalRatingsData ||
    !ageGroupsData
  ) {
    return <LoadingModal />
  }

  if (account.profiles.length === PROFILES.maxNumber) {
    // Redirect to the management modal if the user has reached the maximum number of profiles
    return (
      <Redirect
        to={getModalLocation(location, MODALS.qsParams.profilesManagement)}
      />
    )
  }

  return (
    <ProfilesForm
      ageGroupOptions={ageGroupOptions}
      avatars={appConfig.avatars}
      birthYearOptions={birthYearOptions}
      control={control}
      error={formError}
      errors={errors}
      handleSubmit={handleSubmit(onSubmit)}
      isAvatarSelected={isAvatarSelected}
      isSubmitted={isSubmitted}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      parentalRatingOptions={parentalRatingOptions}
      parentalRatingsData={parentalRatingsData}
      setIsAvatarSelected={setIsAvatarSelected}
      setValue={setValue}
      touchedFields={touchedFields}
      header="Add Profile"
    />
  )
}

AddProfiles.propTypes = {
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    state: PropTypes.shape({
      from: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
    })
  }).isRequired
}

export default AddProfiles
